import axiosInstance from './axiosInstance'

// Tipos para la respuesta de Ofima
export interface OfimaProducto {
  IdProducto: string
  NombreProducto: string
  Saldo: number
  SaldoDisponible: number
  SumaSaldo: number
  IdUnidad: string
  PctIVA: number
  Bigcenter: number | null
  Distrito: number | null
  Venta_General: number | null
  Mercadolibre: number | null
  Falabella: number | null
  Homecenter: number | null
  Exito: number | null
  Euros: number | null
}

export interface OfimaResponse {
  productos: OfimaProducto[]
}

// Tipos para el progreso de sincronización
export interface SyncProgress {
  currentBatch: number
  totalBatches: number
  processedProducts: number
  totalProducts: number
  successCount: number
  errorCount: number
  currentBatchSize: number
  estimatedProgress: string
}

export interface SyncResult {
  success: boolean
  message: string
  totalProcessed: number
  successCount: number
  errorCount: number
  errors: Array<{
    sku: string
    error: string
  }>
}

class MassSyncService {
  private readonly BATCH_SIZE = 15

  /**
   * Obtiene todos los productos de Ofima
   * @returns Promise con la respuesta de Ofima
   */
  async getOfimaProducts(): Promise<OfimaResponse> {
    try {
      const response = await axiosInstance.get<OfimaResponse>('/products/ofima/all')
      return response.data
    } catch (error: any) {
      console.error('Error obteniendo productos de Ofima:', error)
      
      if (error.response?.status === 500) {
        throw new Error('Error interno del servidor al consultar Ofima')
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      
      throw error
    }
  }

  /**
   * Obtiene el precio según la jerarquía de prioridad
   * @param product - Producto de Ofima
   * @returns Precio seleccionado o null si no hay precio válido
   */
  private getPriceByPriority(product: OfimaProducto): number | null {
    // Jerarquía de precios:
    // 1. Bigcenter
    // 2. Distrito  
    // 3. Venta_General
    // 4. Falabella
    // 5. Mercadolibre
    // 6. Homecenter
    
    const priceSources = [
      { name: 'Bigcenter', price: product.Bigcenter },
      { name: 'Distrito', price: product.Distrito },
      { name: 'Venta_General', price: product.Venta_General },
      { name: 'Falabella', price: product.Falabella },
      { name: 'Mercadolibre', price: product.Mercadolibre },
      { name: 'Homecenter', price: product.Homecenter }
    ]
    
    // Buscar el primer precio válido (no null, no undefined, mayor a 0)
    for (const source of priceSources) {
      if (source.price !== null && source.price !== undefined && source.price > 0) {
        console.log(`SKU ${product.IdProducto}: Usando precio de ${source.name} = ${source.price}`)
        return source.price
      }
    }
    
    console.log(`SKU ${product.IdProducto}: No tiene precio válido en ninguna fuente`)
    return null
  }

  /**
   * Transforma productos de Ofima al formato requerido por el endpoint /api/products
   * @param ofimaProducts - Array de productos de Ofima
   * @returns Array de productos en el formato correcto
   */
  private transformOfimaProducts(ofimaProducts: OfimaProducto[]): any[] {
    return ofimaProducts
      .map(product => {
        const price = this.getPriceByPriority(product)
        
        // Solo incluir productos que tengan un precio válido
        if (price === null) {
          return null
        }
        
        return {
          sku: product.IdProducto,
          price: price,
          // Mantener otros campos si es necesario
          name: product.NombreProducto,
          stock: product.Saldo || 0
        }
      })
      .filter(product => product !== null) // Filtrar productos sin precio
  }

  /**
   * Divide un array en lotes del tamaño especificado
   * @param array - Array a dividir
   * @param batchSize - Tamaño del lote
   * @returns Array de lotes
   */
  private createBatches<T>(array: T[], batchSize: number): T[][] {
    const batches: T[][] = []
    for (let i = 0; i < array.length; i += batchSize) {
      batches.push(array.slice(i, i + batchSize))
    }
    return batches
  }

  /**
   * Actualiza un lote de productos usando el endpoint /api/products
   * @param batch - Lote de productos a actualizar
   * @returns Resultado de la actualización
   */
  private async updateBatch(batch: any[]): Promise<{ success: boolean; errors: any[] }> {
    try {
      const response = await axiosInstance.put('/products', batch)
      
      // El endpoint puede devolver información sobre productos omitidos o errores
      const result = response.data
      const errors: any[] = []
      
      // Si hay productos omitidos, los consideramos como errores
      if (result.skippedProducts && result.skippedProducts.length > 0) {
        console.log('Productos omitidos:', result.skippedProducts)
        result.skippedProducts.forEach((skipped: any) => {
          console.log('Agregando error para SKU:', skipped.sku, 'Razón:', skipped.reason)
          errors.push({
            sku: skipped.sku,
            error: skipped.reason || 'Producto omitido'
          })
        })
      }
      
      return { success: true, errors }
    } catch (error: any) {
      console.error('Error actualizando lote:', error)
      
      // Si falla todo el lote, marcar todos como errores
      console.log('Lote falló, creando errores para:', batch)
      const errors = batch.map(product => {
        console.log('Creando error para producto:', product)
        return {
          sku: product.sku,
          error: error.response?.data?.message || 'Error en el lote'
        }
      })
      
      return { success: false, errors }
    }
  }

  /**
   * Ejecuta la sincronización masiva con callback de progreso
   * @param onProgress - Callback que se ejecuta en cada actualización de progreso
   * @returns Promise con el resultado final
   */
  async syncAllProducts(
    onProgress?: (progress: SyncProgress) => void
  ): Promise<SyncResult> {
    try {
      // 1. Obtener productos de Ofima
      console.log('🔄 Obteniendo productos de Ofima...')
      const ofimaResponse = await this.getOfimaProducts()
      
      if (!ofimaResponse.productos || ofimaResponse.productos.length === 0) {
        return {
          success: false,
          message: 'No se encontraron productos en Ofima',
          totalProcessed: 0,
          successCount: 0,
          errorCount: 0,
          errors: []
        }
      }

      // 2. Transformar productos al formato correcto
      console.log('🔄 Transformando productos...')
      const transformedProducts = this.transformOfimaProducts(ofimaResponse.productos)
      
      if (transformedProducts.length === 0) {
        return {
          success: false,
          message: 'No se encontraron productos con precios válidos en Ofima',
          totalProcessed: 0,
          successCount: 0,
          errorCount: 0,
          errors: []
        }
      }

      // 3. Crear lotes
      const batches = this.createBatches(transformedProducts, this.BATCH_SIZE)
      const totalProducts = transformedProducts.length
      const totalBatches = batches.length

      console.log(`🔄 Procesando ${totalProducts} productos en ${totalBatches} lotes de ${this.BATCH_SIZE}`)

      // 4. Procesar lotes
      let processedProducts = 0
      let successCount = 0
      let errorCount = 0
      const allErrors: any[] = []

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i]
        const currentBatch = i + 1

        // Actualizar progreso
        if (onProgress) {
          const progress: SyncProgress = {
            currentBatch,
            totalBatches,
            processedProducts,
            totalProducts,
            successCount,
            errorCount,
            currentBatchSize: batch.length,
            estimatedProgress: `${Math.round((processedProducts / totalProducts) * 100)}%`
          }
          onProgress(progress)
        }

        // Procesar lote
        const result = await this.updateBatch(batch)
        
        if (result.success) {
          successCount += batch.length - result.errors.length
          errorCount += result.errors.length
          allErrors.push(...result.errors)
        } else {
          errorCount += batch.length
          allErrors.push(...result.errors)
        }

        processedProducts += batch.length

        // Pequeña pausa entre lotes para no sobrecargar el servidor
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      // 5. Resultado final
      const finalProgress: SyncProgress = {
        currentBatch: totalBatches,
        totalBatches,
        processedProducts,
        totalProducts,
        successCount,
        errorCount,
        currentBatchSize: 0,
        estimatedProgress: '100%'
      }

      if (onProgress) {
        onProgress(finalProgress)
      }

      return {
        success: true,
        message: `Sincronización completada. ${successCount} productos actualizados, ${errorCount} errores`,
        totalProcessed: processedProducts,
        successCount,
        errorCount,
        errors: allErrors
      }

    } catch (error: any) {
      console.error('Error en sincronización masiva:', error)
      
      return {
        success: false,
        message: error.message || 'Error inesperado durante la sincronización',
        totalProcessed: 0,
        successCount: 0,
        errorCount: 0,
        errors: []
      }
    }
  }
}

const massSyncService = new MassSyncService()
export default massSyncService
